import { useCallback, useEffect, useRef, useState } from "react";
import { warn } from "@/lib/log";
import { addPendingPhoto, listPendingPhotos, removePendingPhoto } from "@/lib/offlineDrafts";

const withPreview = (p) => ({ ...p, section: p.section || "property", tag: p.tag || "other", previewUrl: URL.createObjectURL(p.blob) });
const revoke = (p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl);

/* Photos captured offline for one lead: persisted in IndexedDB, exposed with object-URL previews. */
export default function useOfflinePhotoQueue(leadId) {
  const [pending, setPending] = useState([]);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  useEffect(() => {
    let alive = true;
    listPendingPhotos(leadId)
      .then((rows) => { if (alive) setPending(rows.map(withPreview)); })
      .catch((err) => warn("pending photos unavailable", err));
    return () => { alive = false; };
  }, [leadId]);
  useEffect(() => () => pendingRef.current.forEach(revoke), []);

  const enqueue = useCallback(async (section, file, tag) => {
    const local_id = await addPendingPhoto({ lead_id: leadId, blob: file, filename: file.name, mime: file.type, section, tag });
    setPending((prev) => [...prev, withPreview({ local_id, lead_id: leadId, section, tag, blob: file, filename: file.name, mime: file.type })]);
  }, [leadId]);

  const drop = useCallback(async (local_id) => {
    await removePendingPhoto(local_id);
    const target = pendingRef.current.find((p) => p.local_id === local_id);
    if (target) revoke(target);
    setPending((prev) => prev.filter((x) => x.local_id !== local_id));
  }, []);

  const pendingFor = useCallback((section) => pending.filter((p) => p.section === section), [pending]);

  return { pending, pendingRef, enqueue, drop, pendingFor };
}
